import React, { useState } from "react";
import {
  ArrowLeft, Save, Loader2, FileDown, LayoutGrid,
  RotateCcw, RotateCw, Crosshair, Cloud, Check, Share2, Ruler, Grid3x3,
} from "lucide-react";
import { PANELS, timeAgo } from "./constants";

const EditorTopBar = ({
  setup,
  panel,
  onSelectPanel,
  hasSpine = true,
  saving,
  lastSavedAt,
  foundLostCount,
  onFindLost,
  onUndo,
  onRedo,
  onAssemblePreview,
  onExportFullPDF,
  onExportFullPNG,
  onExportFront,
  exporting,
  onSave,
  savedId,
  onBack,
  isFreePlan,
  onShare,
  onResize,
  showTemplate,
  onToggleTemplate,
}) => {
  const [exportOpen, setExportOpen] = useState(false);
  return (
    <div className="bg-white border-b border-[#D3D1C7] px-4 flex items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <button
          onClick={onBack}
          className="p-1.5 rounded-md hover:bg-[#F1EFE8] text-[#5F5E5A]"
          data-testid="editor-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="text-sm font-medium text-[#2C2C2A]" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>
          {setup.book_title || "Untitled"}{" "}
          <span className="text-[#5F5E5A]">·</span>{" "}
          <span className="text-[#5F5E5A] text-xs uppercase tracking-wider">{setup.trim_size}</span>
        </div>
      </div>

      <div className="flex items-center gap-1 bg-[#F1EFE8] rounded-[8px] p-1">
        {PANELS.filter((p) => hasSpine || p.id !== "spine").map((p) => (
          <button
            key={p.id}
            onClick={() => onSelectPanel(p.id)}
            data-testid={`panel-tab-${p.id}`}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              panel === p.id ? "bg-white text-[#534AB7] shadow-sm" : "text-[#5F5E5A] hover:text-[#2C2C2A]"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        {(saving || lastSavedAt || foundLostCount !== null) && (
          <span className="text-[11px] text-[#5F5E5A] hidden md:inline-flex items-center gap-1.5" data-testid="save-indicator">
            {saving ? (
              <><Cloud className="w-3 h-3 animate-pulse" /> Saving…</>
            ) : foundLostCount !== null ? (
              <><Check className="w-3 h-3 text-[#1D9E75]" /> Recentred {foundLostCount} object{foundLostCount === 1 ? "" : "s"}</>
            ) : (
              <><Cloud className="w-3 h-3 text-[#1D9E75]" /> Saved {timeAgo(lastSavedAt)}</>
            )}
          </span>
        )}

        <button
          onClick={onFindLost}
          data-testid="find-lost-btn"
          title="Recentre any objects dragged off-canvas"
          className="p-2 rounded-md hover:bg-[#F1EFE8] text-[#5F5E5A]"
        >
          <Crosshair className="w-4 h-4" />
        </button>
        <button onClick={onUndo} className="p-2 rounded-md hover:bg-[#F1EFE8] text-[#5F5E5A]" title="Undo (Ctrl+Z)" data-testid="undo-btn">
          <RotateCcw className="w-4 h-4" />
        </button>
        <button onClick={onRedo} className="p-2 rounded-md hover:bg-[#F1EFE8] text-[#5F5E5A]" title="Redo (Ctrl+Y)" data-testid="redo-btn">
          <RotateCw className="w-4 h-4" />
        </button>

        <button
          onClick={onAssemblePreview}
          className="px-3 py-1.5 rounded-[8px] border border-[#D3D1C7] text-sm hover:bg-[#F1EFE8] flex items-center gap-1.5"
          data-testid="assemble-preview-btn"
        >
          <LayoutGrid className="w-3.5 h-3.5" /> Assemble & Preview
        </button>

        {onToggleTemplate && (
          <button
            onClick={onToggleTemplate}
            data-testid="toggle-template-btn"
            title={showTemplate ? "Hide KDP template overlay (bleed / trim / safe / barcode)" : "Show KDP template overlay (bleed / trim / safe / barcode)"}
            className={`px-3 py-1.5 rounded-[8px] border text-sm flex items-center gap-1.5 transition-colors ${
              showTemplate
                ? "border-[#534AB7] bg-[#534AB7]/[0.06] text-[#534AB7]"
                : "border-[#D3D1C7] text-[#2C2C2A] hover:bg-[#F1EFE8]"
            }`}
          >
            <Grid3x3 className="w-3.5 h-3.5" /> Template
          </button>
        )}

        <button
          onClick={onShare}
          className="px-3 py-1.5 rounded-[8px] border border-[#D3D1C7] text-sm hover:bg-[#F1EFE8] flex items-center gap-1.5"
          data-testid="share-btn"
          title="Generate a public preview link"
        >
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>

        {onResize && (
          <button
            onClick={onResize}
            className="px-3 py-1.5 rounded-[8px] border border-[#D3D1C7] text-sm hover:bg-[#FFF4E5] hover:border-[#BA7517] hover:text-[#7A5C0E] flex items-center gap-1.5"
            data-testid="resize-kdp-btn"
            title="Adjust page count or trim size if KDP rejected the cover for wrong dimensions"
          >
            <Ruler className="w-3.5 h-3.5" /> Resize for KDP
          </button>
        )}

        <div className="relative">
          <button
            onClick={() => setExportOpen((s) => !s)}
            className="px-3 py-1.5 rounded-[8px] border border-[#D3D1C7] text-sm hover:bg-[#F1EFE8] flex items-center gap-1.5"
            data-testid="export-btn"
          >
            <FileDown className="w-3.5 h-3.5" /> Export
          </button>
          {exportOpen && (
            <div className="absolute right-0 mt-1 bg-white border border-[#D3D1C7] rounded-[8px] shadow-lg w-64 py-1 z-30" data-testid="export-menu">
              <button
                onClick={() => { setExportOpen(false); onExportFullPDF(); }}
                disabled={exporting}
                data-testid="export-full-pdf-btn"
                className="w-full px-3 py-2 text-left text-sm hover:bg-[#F1EFE8] flex items-center gap-2 disabled:opacity-50"
              >
                <FileDown className="w-3.5 h-3.5 text-[#534AB7]" />
                <div>
                  <div className="font-medium">Full wrap — PDF</div>
                  <div className="text-[11px] text-[#5F5E5A]">For KDP print upload (300 DPI)</div>
                </div>
              </button>
              <button
                onClick={() => { setExportOpen(false); onExportFullPNG(); }}
                disabled={exporting}
                data-testid="export-full-png-btn"
                className="w-full px-3 py-2 text-left text-sm hover:bg-[#F1EFE8] flex items-center gap-2 disabled:opacity-50"
              >
                <FileDown className="w-3.5 h-3.5 text-[#534AB7]" />
                <div>
                  <div className="font-medium">Full wrap — PNG</div>
                  <div className="text-[11px] text-[#5F5E5A]">High-res image (300 DPI)</div>
                </div>
              </button>
              <div className="border-t border-[#D3D1C7] my-1" />
              <button
                onClick={() => { onExportFront("jpeg"); setExportOpen(false); }}
                data-testid="export-front-jpg-btn"
                className="w-full px-3 py-2 text-left text-sm hover:bg-[#F1EFE8] flex items-center gap-2"
              >
                <FileDown className="w-3.5 h-3.5 text-[#5F5E5A]" />
                <div>
                  <div className="font-medium">Front cover — JPG</div>
                  <div className="text-[11px] text-[#5F5E5A]">For KDP ebook upload</div>
                </div>
              </button>
              <button
                onClick={() => { onExportFront("png"); setExportOpen(false); }}
                data-testid="export-front-png-btn"
                className="w-full px-3 py-2 text-left text-sm hover:bg-[#F1EFE8] flex items-center gap-2"
              >
                <FileDown className="w-3.5 h-3.5 text-[#5F5E5A]" />
                <div>
                  <div className="font-medium">Front cover — PNG</div>
                </div>
              </button>
              {isFreePlan && (
                <div className="px-3 py-2 mt-1 bg-[#FAFAF9] text-[11px] text-[#5F5E5A] border-t border-[#D3D1C7]">
                  Free plan adds a "Created with PublisherKit" watermark.
                </div>
              )}
            </div>
          )}
        </div>

        <button
          onClick={onSave}
          disabled={saving}
          data-testid="save-project-btn"
          className="px-4 py-1.5 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] disabled:opacity-50 flex items-center gap-1.5"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {savedId ? "Saved" : "Save"}
        </button>
      </div>
    </div>
  );
};

export default EditorTopBar;
